/**
 * Maps every DevRobot state vocabulary value (shared/states.ts) to a tone and a human label.
 * The same words appear in several vocabularies with different meanings ("running" is good
 * for a process but merely "in progress" for a job; "pending" is a warning for DNS but
 * neutral for a stage), so callers always name the vocabulary.
 */
export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'muted';

export type StatusKind =
  | 'job'
  | 'stage'
  | 'process'
  | 'health'
  | 'dns'
  | 'cert'
  | 'backup'
  | 'connection'
  | 'enrollment'
  | 'registration'
  | 'config'
  | 'outcome'
  | 'file'
  | 'container'
  | 'migration'
  | 'artifact';

export interface StatusInfo {
  tone: Tone;
  label: string;
  /** Longer explanation, shown as a tooltip/title. */
  hint?: string;
}

const s = (tone: Tone, label: string, hint?: string): StatusInfo => ({ tone, label, hint });

export const STATUS_MAP: Record<StatusKind, Record<string, StatusInfo>> = {
  job: {
    queued: s('neutral', 'queued'),
    running: s('info', 'running'),
    awaiting_approval: s('warning', 'awaiting approval'),
    succeeded: s('success', 'succeeded'),
    failed: s('error', 'failed'),
    canceled: s('warning', 'canceled'),
    unknown: s('warning', 'unknown', 'DevRobot lost contact while the job ran; the outcome has not been confirmed yet.'),
  },
  stage: {
    pending: s('muted', 'pending'),
    running: s('info', 'running'),
    succeeded: s('success', 'succeeded'),
    failed: s('error', 'failed'),
    skipped: s('muted', 'skipped'),
    canceled: s('warning', 'canceled'),
  },
  process: {
    running: s('success', 'running'),
    partial: s('warning', 'partially running', 'Some services are running and some are not.'),
    stopped: s('neutral', 'stopped'),
    'not-deployed': s('muted', 'not deployed'),
    unknown: s('warning', 'unknown', 'Not currently observable. The application may still be running.'),
  },
  health: {
    healthy: s('success', 'healthy'),
    unhealthy: s('error', 'unhealthy'),
    starting: s('info', 'starting'),
    unknown: s('warning', 'unknown', 'Health could not be observed.'),
    none: s('muted', 'no health check'),
  },
  dns: {
    ok: s('success', 'ok'),
    pending: s('warning', 'pending', 'The required DNS record does not resolve yet.'),
    mismatch: s('error', 'mismatch', 'The hostname resolves to a different address.'),
    unknown: s('muted', 'unknown', 'DNS has not been checked.'),
  },
  cert: {
    valid: s('success', 'valid'),
    pending: s('warning', 'pending', 'The certificate has not been issued yet.'),
    failed: s('error', 'failed'),
    unknown: s('muted', 'unknown'),
    'n/a': s('muted', 'not applicable'),
  },
  backup: {
    creating: s('info', 'creating'),
    failed: s('error', 'failed'),
    created: s('neutral', 'created (unverified)', 'Exported but not yet verified; it cannot be restored until verified.'),
    verified: s('success', 'verified'),
    expired: s('muted', 'expired'),
    removed: s('muted', 'removed'),
  },
  connection: {
    connected: s('success', 'connected'),
    disconnected: s('error', 'disconnected', 'The agent is not connected. Applications may still be running.'),
    stale: s('warning', 'stale', 'The last observation is older than expected; data shown is not live.'),
    maintenance: s('warning', 'maintenance'),
    never: s('muted', 'never connected'),
  },
  enrollment: {
    registered: s('neutral', 'registered'),
    validating: s('info', 'validating'),
    awaiting_approval: s('warning', 'awaiting approval'),
    preparing: s('info', 'preparing'),
    ready: s('success', 'ready'),
    preparation_failed: s('error', 'preparation failed'),
    removed: s('muted', 'removed'),
  },
  registration: {
    local: s('muted', 'local only'),
    pending: s('info', 'pending'),
    registered: s('success', 'registered'),
    failed: s('error', 'failed'),
  },
  config: {
    set: s('success', 'set'),
    empty: s('info', 'empty (intentional)', 'Deliberately set to an empty value.'),
    missing: s('error', 'missing', 'Required and not set: deployments are blocked until it is provided.'),
    'missing-optional': s('muted', 'not set (optional)'),
    'not-applicable': s('muted', 'not applicable', 'Not used in this environment.'),
  },
  outcome: {
    success: s('success', 'success'),
    failure: s('error', 'failure'),
    denied: s('warning', 'denied'),
  },
  file: {
    set: s('success', 'provided'),
    missing: s('error', 'missing'),
  },
  container: {
    running: s('success', 'running'),
    completed: s('success', 'completed', 'Setup service: it ran once and exited successfully.'),
    exited: s('neutral', 'exited'),
    restarting: s('warning', 'restarting'),
    created: s('muted', 'created'),
    paused: s('warning', 'paused'),
    dead: s('error', 'dead'),
    removing: s('muted', 'removing'),
    healthy: s('success', 'healthy'),
    unhealthy: s('error', 'unhealthy'),
    starting: s('info', 'starting'),
    none: s('muted', 'no health check'),
    unknown: s('warning', 'unknown'),
  },
  migration: {
    pending: s('neutral', 'pending'),
    running: s('info', 'running'),
    succeeded: s('success', 'succeeded'),
    failed: s('error', 'failed'),
    canceled: s('warning', 'canceled'),
    unknown: s('warning', 'unknown', 'DevRobot lost track of the migration; both targets stay locked until it is resolved or abandoned.'),
  },
  artifact: {
    uploading: s('info', 'uploading'),
    stored: s('success', 'stored'),
    imported: s('success', 'imported'),
    failed: s('error', 'failed'),
    expired: s('muted', 'expired'),
  },
};

export function statusInfo(kind: StatusKind, value: string | null | undefined): StatusInfo {
  const v = value ?? 'unknown';
  return STATUS_MAP[kind][v] ?? s('neutral', v.replace(/[_-]+/g, ' '));
}

export const BADGE_CLASS: Record<Tone, string> = {
  success: 'badge badge-soft badge-success',
  warning: 'badge badge-soft badge-warning',
  error: 'badge badge-soft badge-error',
  info: 'badge badge-soft badge-info',
  neutral: 'badge badge-soft badge-neutral',
  muted: 'badge badge-ghost',
};

/** Outline variant used for last-known (stale) values so they never look live. */
export const BADGE_OUTLINE_CLASS: Record<Tone, string> = {
  success: 'badge badge-outline badge-success',
  warning: 'badge badge-outline badge-warning',
  error: 'badge badge-outline badge-error',
  info: 'badge badge-outline badge-info',
  neutral: 'badge badge-outline badge-neutral',
  muted: 'badge badge-outline',
};

export const DOT_CLASS: Record<Tone, string> = {
  success: 'status status-success',
  warning: 'status status-warning',
  error: 'status status-error',
  info: 'status status-info',
  neutral: 'status status-neutral',
  muted: 'status',
};
